/**
 * Stage D2 — Kitchen Production & Operations analytics vocabulary.
 * Presentation constants and DTO types only; the database RPCs
 * (`admin_kitchen_production`, `admin_ops_production_report`) remain the
 * single source of truth for the numbers.
 */
import { z } from "zod";
import { ORDER_SOURCES } from "./ops-sources";

export const MEAL_SLOTS = ["breakfast", "lunch", "snacks", "dinner"] as const;
export type ProductionSlot = (typeof MEAL_SLOTS)[number];

export const MEAL_SLOT_LABEL: Record<string, string> = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  snacks: "Snacks",
  dinner: "Dinner",
};

export const PRODUCTION_CATEGORIES = [
  "alacarte",
  "meal_plans",
  "soups",
  "salads",
  "beverages",
  "products",
] as const;
export type ProductionCategory = (typeof PRODUCTION_CATEGORIES)[number];

export const PRODUCTION_CATEGORY_LABEL: Record<string, string> = {
  alacarte: "À la Carte",
  meal_plans: "Meal Plans",
  soups: "Soups",
  salads: "Salads",
  beverages: "Beverages",
  products: "Products",
};

export const VEG_FILTERS = ["veg", "non_veg"] as const;

export const DELIVERY_STATES = [
  "pending",
  "out_for_delivery",
  "delivered",
  "failed",
  "returned",
] as const;
export type DeliveryState = (typeof DELIVERY_STATES)[number];

export const DELIVERY_STATE_LABEL: Record<DeliveryState, string> = {
  pending: "Pending",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  failed: "Failed",
  returned: "Returned",
};

// ------------------------------------------------------------ validation

export const productionInputSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  slot: z.enum(MEAL_SLOTS).optional(),
  category: z.enum(PRODUCTION_CATEGORIES).optional(),
  area: z.string().trim().max(120).optional(),
  veg: z.enum(VEG_FILTERS).optional(),
  source: z.enum(ORDER_SOURCES).optional(),
});
export type ProductionInput = z.infer<typeof productionInputSchema>;

export const preparedQtySchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  slot: z.string().trim().min(1).max(30),
  kind: z.string().trim().min(1).max(30),
  key: z.string().trim().min(1).max(300),
  qty: z.number().int().min(0).max(100000),
});

export const dayRangeSchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export const dispatchSchema = z.object({
  orderId: z.string().uuid(),
  driver: z.string().trim().max(120).optional(),
  vehicle: z.string().trim().max(60).optional(),
  /** Master-data references from the Delivery Management module. */
  driverId: z.string().uuid().nullable().optional(),
  vehicleId: z.string().uuid().nullable().optional(),
  state: z.enum(DELIVERY_STATES).optional(),
  notes: z.string().trim().max(500).optional(),
  markDispatched: z.boolean().default(false),
});

// ----------------------------------------------------------------- types

export type ProductionSourceBreakdown = {
  website: number;
  phone: number;
  walkin: number;
  whatsapp: number;
  corporate: number;
  subscription: number;
  other: number;
};

export type ProductionDish = {
  item_key: string;
  item_kind: string;
  category: string;
  slot: string;
  title: string;
  ordered: number;
  prepared: number;
  pending: number;
  remaining: number;
  sources: ProductionSourceBreakdown;
};

export type ProductionBucket = {
  ordered: number;
  prepared: number;
  remaining: number;
};

export type KitchenProduction = {
  date: string;
  totals: ProductionBucket & { items: number };
  slots: (ProductionBucket & { slot: string })[];
  categories: (ProductionBucket & { category: string })[];
  dishes: ProductionDish[];
  areas: string[];
};

export type DispatchRow = {
  id: string;
  order_ref: string;
  customer_name: string;
  phone: string;
  neighbourhood: string;
  address: string;
  status: string;
  order_channel: string;
  preferred_delivery_date: string;
  preferred_delivery_time: string;
  line_count: number;
  total_amount_paise: number | null;
  payment_status: string | null;
  driver_name: string;
  vehicle_number: string;
  driver_id: string | null;
  vehicle_id: string | null;
  dispatched_at: string | null;
  delivered_at: string | null;
  delivery_state: DeliveryState;
  notes: string;
};

export type RefundRow = {
  id: string;
  order_ref: string;
  customer_name: string;
  phone: string;
  status: string;
  payment_status: string | null;
  payment_amount_paise: number | null;
  total_amount_paise: number | null;
  razorpay_payment_id: string | null;
  preferred_delivery_date: string;
  updated_at: string;
  delivery_state: DeliveryState | null;
};

export type ProductionReport = {
  range: { from: string; to: string };
  daily: { day: string; orders: number; revenue_paise: number }[];
  by_source: { source: string; orders: number; revenue_paise: number }[];
  by_status: { status: string; orders: number }[];
  by_dish: { title: string; kind: string; quantity: number }[];
  by_slot: { slot: string; quantity: number }[];
  kitchen: { total_items: number; total_orders: number; revenue_paise: number };
};

export function istToday(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
